// components/movements/CreateMovementModal.tsx
import React, { useState, useEffect } from "react";
import { CreateMovementRequest } from "../../models/Movement";
import { useUser } from '../../components/UserContext';
import { useMovementsApi } from "../../hooks/useMovementsApi";
import type { ExtendedInfoType, ArtworkStatusRefData } from "../../models/ArtworkStatus";
import "./MovementsStyleSheet.css";
interface Props {
    artworkId: number;
    artworkTitle: string;
    artistName?: string;
    referenceNumber?: string;
    thumbnailUrl?: string;
    statuses: ArtworkStatusRefData[];
    onClose: () => void;
    onCreated: () => void;
}

const MOVEMENT_CODES = ["LOAN_OUT", "LOAN_IN", "OCONS", "EXHIB", "TRANS", "REST", "STOR", "AUCT"];

const INSURED_BY_OPTIONS = [
    { value: "owner", label: "Owner's Policy" },
    { value: "venue", label: "Venue's Policy" },
    { value: "courier", label: "Courier's Policy" },
];

const CURRENCIES = ["GBP", "EUR", "USD"];

export const CreateMovementModal: React.FC<Props> = ({
    artworkId,
    artworkTitle,
    artistName,
    referenceNumber,
    thumbnailUrl,
    statuses,
    onClose,
    onCreated,
}) => {
    const api = useMovementsApi();
    const { getAccessToken } = useUser();

    const movementStatuses = statuses.filter(s => MOVEMENT_CODES.includes(s.code));

    const [statusId, setStatusId] = useState<number>(movementStatuses[0]?.artworkStatusId ?? 0);
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [to, setTo] = useState("");
    const [contact, setContact] = useState("");
    const [address, setAddress] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [insuredValue, setInsuredValue] = useState("");
    const [insuranceCurrency, setInsuranceCurrency] = useState("GBP");
    const [insurancePolicyRef, setInsurancePolicyRef] = useState("");
    const [insuredBy, setInsuredBy] = useState("owner");
    const [authorisedBy, setAuthorisedBy] = useState("");

    const selectedStatus = movementStatuses.find(s => s.artworkStatusId === statusId);
    const extType: ExtendedInfoType = selectedStatus?.extendedInfoType ?? "NONE";

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose();
    };

    const handleSubmit = async () => {
        if (!statusId) return;
        setSaving(true);
        setError(null);
        try {
            const accessToken = await getAccessToken();

            if (accessToken) {

                const request: CreateMovementRequest = {
                    artworkId,
                    artworkStatusId: statusId,
                    notes: notes || undefined,
                    to: extType === "OUT" || extType === "FROM" ? to : undefined,
                    contact: contact || undefined,
                    address: address || undefined,
                    dateFrom: dateFrom || undefined,
                    dateTo: dateTo || undefined,
                    insuredValue: insuredValue ? Number(insuredValue) : undefined,
                    insuranceCurrency: insuredValue ? insuranceCurrency : undefined,
                    insurancePolicyRef: insurancePolicyRef || undefined,
                    insuredBy: insuredValue ? insuredBy : undefined,
                    authorisedBy: authorisedBy || undefined,
                };
                await api.createMovement(request, accessToken);
                onCreated();
                onClose();
            }
        } catch (e: any) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            {/* Overlay */}
            <div className="cm-overlay" onClick={handleOverlayClick}>

                {/* Dialog — two thirds width */}
                <div className="cm-dialog" role="dialog" aria-modal="true"
                    aria-labelledby="cm-title">

                    {/* Header */}
                    <div className="cm-header">
                        <div className="cm-header-text">
                            <h2 id="cm-title">Record Movement</h2>
                            <p className="cm-header-sub">
                                Create a new loan, exhibition, transit or storage record
                            </p>
                        </div>
                        <button className="cm-close" onClick={onClose}
                            aria-label="Close">×</button>
                    </div>

                    {/* Body — two column layout */}
                    <div className="cm-body">

                        {/* LEFT — artwork context */}
                        <aside className="cm-sidebar">
                            {thumbnailUrl ? (
                                <img src={thumbnailUrl} alt={artworkTitle}
                                    className="cm-thumb" />
                            ) : (
                                <div className="cm-thumb-placeholder">
                                    <span>No Image</span>
                                </div>
                            )}
                            <div className="cm-artwork-meta">
                                <div className="cm-artwork-title">{artworkTitle}</div>
                                {artistName && (
                                    <div className="cm-artwork-artist">{artistName}</div>
                                )}
                                {referenceNumber && (
                                    <div className="cm-artwork-ref">{referenceNumber}</div>
                                )}
                            </div>

                            {/* Movement type selector in sidebar */}
                            <div className="cm-form-group">
                                <label className="cm-label">Movement Type</label>
                                <select className="cm-select" value={statusId}
                                    onChange={e => setStatusId(Number(e.target.value))}>
                                    {movementStatuses.map(s => (
                                        <option key={s.artworkStatusId}
                                            value={s.artworkStatusId}>
                                            {s.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {selectedStatus && (
                                <div className="cm-status-hint">
                                    <span className={`badge badge--movement-${extType.toLowerCase()}`}>
                                        {selectedStatus.name}
                                    </span>
                                </div>
                            )}
                        </aside>

                        {/* RIGHT — form fields */}
                        <div className="cm-form">

                            {error && (
                                <div className="cm-error">{error}</div>
                            )}

                            {/* OUT — Loan Out, Exhibition, Transit, Restoration, Storage */}
                            {extType === "OUT" && (
                                <>
                                    <fieldset className="cm-fieldset">
                                        <legend>Destination Details</legend>

                                        <div className="cm-form-group">
                                            <label className="cm-label">
                                                Destination Name <span className="cm-required">*</span>
                                            </label>
                                            <input className="cm-input" value={to}
                                                onChange={e => setTo(e.target.value)}
                                                placeholder="Gallery / institution / facility" />
                                        </div>

                                        <div className="cm-form-row">
                                            <div className="cm-form-group">
                                                <label className="cm-label">Contact</label>
                                                <input className="cm-input" value={contact}
                                                    onChange={e => setContact(e.target.value)} />
                                            </div>
                                            <div className="cm-form-group">
                                                <label className="cm-label">Authorised By</label>
                                                <input className="cm-input" value={authorisedBy}
                                                    onChange={e => setAuthorisedBy(e.target.value)} />
                                            </div>
                                        </div>

                                        <div className="cm-form-group">
                                            <label className="cm-label">Address</label>
                                            <input className="cm-input" value={address}
                                                onChange={e => setAddress(e.target.value)} />
                                        </div>

                                        <div className="cm-form-row">
                                            <div className="cm-form-group">
                                                <label className="cm-label">Departure Date</label>
                                                <input className="cm-input" type="date"
                                                    value={dateFrom}
                                                    onChange={e => setDateFrom(e.target.value)} />
                                            </div>
                                            <div className="cm-form-group">
                                                <label className="cm-label">Expected Return</label>
                                                <input className="cm-input" type="date"
                                                    value={dateTo}
                                                    onChange={e => setDateTo(e.target.value)} />
                                            </div>
                                        </div>
                                    </fieldset>

                                    <fieldset className="cm-fieldset">
                                        <legend>Insurance During Movement</legend>

                                        <div className="cm-form-row">
                                            <div className="cm-form-group cm-form-group--grow">
                                                <label className="cm-label">Insured Value</label>
                                                <input className="cm-input" type="number"
                                                    value={insuredValue}
                                                    onChange={e => setInsuredValue(e.target.value)}
                                                    placeholder="0" />
                                            </div>
                                            <div className="cm-form-group cm-form-group--narrow">
                                                <label className="cm-label">Currency</label>
                                                <select className="cm-select"
                                                    value={insuranceCurrency}
                                                    onChange={e => setInsuranceCurrency(e.target.value)}>
                                                    {CURRENCIES.map(c => (
                                                        <option key={c} value={c}>{c}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <div className="cm-form-row">
                                            <div className="cm-form-group">
                                                <label className="cm-label">Policy Reference</label>
                                                <input className="cm-input"
                                                    value={insurancePolicyRef}
                                                    onChange={e => setInsurancePolicyRef(e.target.value)} />
                                            </div>
                                            <div className="cm-form-group">
                                                <label className="cm-label">Insured By</label>
                                                <select className="cm-select" value={insuredBy}
                                                    onChange={e => setInsuredBy(e.target.value)}>
                                                    {INSURED_BY_OPTIONS.map(o => (
                                                        <option key={o.value} value={o.value}>
                                                            {o.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </fieldset>
                                </>
                            )}

                            {/* FROM — Loan In */}
                            {extType === "FROM" && (
                                <fieldset className="cm-fieldset">
                                    <legend>Source Details</legend>

                                    <div className="cm-form-group">
                                        <label className="cm-label">
                                            Incoming From <span className="cm-required">*</span>
                                        </label>
                                        <input className="cm-input" value={to}
                                            onChange={e => setTo(e.target.value)}
                                            placeholder="Lender / institution name" />
                                    </div>

                                    <div className="cm-form-group">
                                        <label className="cm-label">Contact</label>
                                        <input className="cm-input" value={contact}
                                            onChange={e => setContact(e.target.value)} />
                                    </div>

                                    <div className="cm-form-row">
                                        <div className="cm-form-group">
                                            <label className="cm-label">Date Received</label>
                                            <input className="cm-input" type="date"
                                                value={dateFrom}
                                                onChange={e => setDateFrom(e.target.value)} />
                                        </div>
                                        <div className="cm-form-group">
                                            <label className="cm-label">Return To Owner By</label>
                                            <input className="cm-input" type="date"
                                                value={dateTo}
                                                onChange={e => setDateTo(e.target.value)} />
                                        </div>
                                    </div>
                                </fieldset>
                            )}

                            {/* NONE — no extended fields, just notes */}
                            {extType === "NONE" && (
                                <p className="cm-hint">
                                    No additional details required for this movement type.
                                </p>
                            )}

                            {/* Notes — always shown */}
                            <div className="cm-form-group">
                                <label className="cm-label">Notes</label>
                                <textarea className="cm-textarea" rows={4}
                                    value={notes}
                                    onChange={e => setNotes(e.target.value)}
                                    placeholder="Any additional context for this movement…" />
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="cm-footer">
                        <button className="btn btn--ghost" onClick={onClose}
                            disabled={saving}>
                            Cancel
                        </button>
                        <button className="btn btn--primary" onClick={handleSubmit}
                            disabled={saving || !statusId || (extType === "OUT" && !to.trim())}>
                            {saving ? "Saving…" : "Record Movement"}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};
export default CreateMovementModal;